import { useMemo } from 'react';
import { planFrames, type CameraPov, type FashionModel, type Pose, type ShotPlanInput } from '@studio/shared';

import { useT, usePresetLabel } from '../lib/i18n';

/**
 * The roll, before it is shot.
 *
 * Setting up a shoot is a multiplication — faces times angles times takes —
 * and a form asks you to do it in your head, then bills you for the answer.
 * This draws it instead: one cell per frame, in the shape they will be, in the
 * order they will be shot, labelled with the angle and the position. The count
 * and the price stop being a number at the bottom of a page and become a thing
 * you can look at and say "no, not that many" about.
 *
 * The frames come from the same `planFrames` the renderer plans the real roll
 * with, so this cannot quietly disagree with what arrives.
 */
interface Props {
  modelIds: string[];
  povIds: string[];
  variantsPerPov: number;
  poseIds: string[];
  models: FashionModel[];
  povs: CameraPov[];
  poses: Pose[];
  aspectRatio: string;
  /** Beyond this the grid is summarised rather than drawn cell by cell. */
  cap?: number;
  /** The lists that multiply each take: scenes, attitudes, expressions. */
  across?: Pick<ShotPlanInput, 'backdropIds' | 'attitudes' | 'expressions'>;
}

export function Planche({
  modelIds,
  povIds,
  variantsPerPov,
  poseIds,
  models,
  povs,
  poses,
  aspectRatio,
  cap = 36,
  across,
}: Props) {
  const t = useT();
  const tp = usePresetLabel();

  const frames = useMemo(
    () => planFrames({ modelIds, povIds, variantsPerPov, poseIds, ...across }),
    [modelIds, povIds, variantsPerPov, poseIds, across],
  );

  if (!frames.length) {
    return (
      <div className="planche planche--empty">
        <p className="faint">{t('plan.empty')}</p>
      </div>
    );
  }

  const shown = frames.slice(0, cap);
  const hidden = frames.length - shown.length;

  // A ratio like "2:3" drives the cell shape, so the grid is the shape of the
  // photographs rather than a grid of squares pretending to be them.
  const [w, h] = aspectRatio.split(':').map(Number);
  const ratio = w && h ? `${w} / ${h}` : '2 / 3';

  /*
   * Grouped by face and then by angle, because that is how a roll is read and
   * how it is shot: her frames, then hers; and within each, the takes of one
   * set-up together. Naming the angle once over its takes rather than in every
   * cell is what makes the label legible at this size — repeated in each cell
   * it truncated to "Full body — …" and said nothing.
   */
  const byModel = modelIds
    .map((id) => {
      const mine = shown.filter((f) => f.modelId === id);
      const setUps: Array<{ povId: string; takes: typeof mine }> = [];
      for (const frame of mine) {
        const last = setUps[setUps.length - 1];
        if (last && last.povId === frame.povId) last.takes.push(frame);
        else setUps.push({ povId: frame.povId, takes: [frame] });
      }
      return { id, model: models.find((m) => m.id === id), setUps };
    })
    .filter((group) => group.setUps.length > 0);

  return (
    <div className="planche">
      {byModel.map((group) => (
        <div key={group.id} className="planche__group">
          {byModel.length > 1 && (
            <span className="planche__who">{group.model?.name ?? t('plan.someone')}</span>
          )}

          {group.setUps.map((setUp, s) => {
            const pov = povs.find((p) => p.id === setUp.povId);
            const angle = tp(`pov.${setUp.povId}.name`, pov?.name ?? setUp.povId);
            return (
              <div key={`${setUp.povId}-${s}`} className="planche__setUp">
                <span className="planche__angle">{angle}</span>
                <div className="planche__grid">
                  {setUp.takes.map((frame, i) => {
                    const pose = frame.poseId ? poses.find((p) => p.id === frame.poseId) : undefined;
                    const poseName = pose ? tp(`pose.${pose.id}.name`, pose.name) : '';
                    return (
                      <div
                        key={`${frame.povId}-${frame.variantIndex}-${i}`}
                        className="planche__cell"
                        style={{ aspectRatio: ratio }}
                        title={poseName ? `${angle} · ${poseName}` : angle}
                      >
                        {poseName && <span className="planche__pose">{poseName}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {hidden > 0 && <p className="planche__more faint">{t('plan.andMore', { n: hidden })}</p>}
    </div>
  );
}
